import { z } from "zod";
import {
  ProductSchema,
  createProductSchema,
  updateProductSchema,
  ProductStatusEnum,
  RockBoardTypeEnum,
  EditorTypeEnum,
  ProductRockBoardSchema,
  ProductCustomSchema,
  stripeProductSchema,
  ProductVehicleTypeSchema,
  ProductAttachmentSchema,
  ProductTagSchema,
  ProductExclusiveCompanySchema,
  ProductBrandSchema,
  ProductPricingTierSchema,
  CompanyProductPartNumberSchema,
  PdfAttachmentSchema,
} from "@/lib/validations/products/product";
import { Design } from "@/types/editor/blocks";

import type { ProductVariant, VariantAttributeValue } from "./attributes";
import { ProductGroup } from "./product-group";

// Base type exports
export type ProductStatus = z.infer<typeof ProductStatusEnum>;
export type RockBoardType = z.infer<typeof RockBoardTypeEnum>;
export type EditorType = z.infer<typeof EditorTypeEnum>;

// Core product types
export type Product = z.infer<typeof ProductSchema>;
export type CreateProductInput = z.infer<typeof createProductSchema>;
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

// Relationship types
export type ProductVehicleType = z.infer<typeof ProductVehicleTypeSchema>;
export type ProductAttachment = z.infer<typeof ProductAttachmentSchema>;
export type ProductExclusiveCompany = z.infer<
  typeof ProductExclusiveCompanySchema
>;
export type ProductTag = z.infer<typeof ProductTagSchema>;
export type ProductRockBoard = z.infer<typeof ProductRockBoardSchema>;
export type ProductCustom = z.infer<typeof ProductCustomSchema>;
export type StripeProduct = z.infer<typeof stripeProductSchema>;
export type ProductBrand = z.infer<typeof ProductBrandSchema>;
export type ProductPricingTier = z.infer<typeof ProductPricingTierSchema>;
export type CompanyProductPartNumber = z.infer<
  typeof CompanyProductPartNumberSchema
>;
export type PdfAttachment = z.infer<typeof PdfAttachmentSchema>;

// Fields that can be edited in a product
export type ProductEditableField =
  | "title"
  | "part_number"
  | "moq"
  | "description"
  | "features"
  | "images"
  | "pdf_attachments"
  | "cost_price"
  | "tax_rate"
  | "weight"
  | "width"
  | "height"
  | "length"
  | "shipping_width"
  | "shipping_height"
  | "shipping_length"
  | "shipping_weight"
  | "meta_title"
  | "meta_description"
  | "editor"
  | "brand_id"
  | "status"
  | "product_pricing_tiers"
  | "vehicle_types"
  | "rock_board"
  | "attachments"
  | "exclusive_companies"
  | "company_part_numbers"
  | "tags"
  | "attribute_values";

// Interface defining permissions for product fields
export interface ProductFieldPermissions {
  editable: ProductEditableField[];
  viewable: ProductEditableField[];
}

// Fields that can be used for sorting products
export const ProductSortable = [
  "title",
  "part_number",
  "moq",
  "cost_price",
  "created",
  "updated",
  "company_part_numbers",
] as const;
export type ProductSortField = (typeof ProductSortable)[number];

// Simplified product type for lists
export type SimpleProduct = {
  id: string;
  title: string;
  image: string[] | null;
};

// Product with all its relationships
export interface ProductWithRelations extends Product {
  product_brand?: ProductBrand;
  vehicle_types: ProductVehicleType[];
  product_pricing_tiers: ProductPricingTier[];
  attachments: ProductAttachment[];
  exclusive_companies: ProductExclusiveCompany[];
  company_part_numbers: CompanyProductPartNumber[];
  rock_board?: ProductRockBoard;
  custom?: ProductCustom;
  tags: ProductTag[];
  attribute_values: VariantAttributeValue[];
  product_groups: ProductGroup[];
  product_variants?: ProductVariant[];
}

export interface DisplayProduct {
  id: string;
  title: string;
  part_number: string;
  moq: number;
  description: string;
  features: string[];
  images: string[];
  hover_image: string | null;
  pdf_attachments: PdfAttachment[] | null;
  tax_rate: number;
  vehicle_types: {
    value: string;
    label: string;
    avatar: string | null;
  }[];
  price: number;
  compare_price: number;
  attachments: {
    product_id: string;
    quantity: number;
  }[];
  attribute_values: {
    id: string;
    value: string;
    label: string;
  }[];
  tags: string[];
  brand: string;
  weight: number;
  width: number | null;
  height: number | null;
  length: number | null;
  shipping_width: number | null;
  shipping_height: number | null;
  shipping_length: number | null;
  shipping_weight: number;
  meta_title: string | null;
  meta_description: string | null;
  editor: EditorType | null;
  parent?: {
    id: string;
    parent_name: string;
    parent_image: string | null;
  };

  // Rock Board
  rock_board?: {
    id: string;
    type: RockBoardType;
    voltage: number | null;
    current: number | null;
  };
  custom?: {
    id: string;
    design_id: string | null;
    min_chars: number | null;
    max_chars: number | null;
    char_height: number | null;
    char_width: number | null;
    bound_width: number | null;
    bound_height: number | null;
    overlay_image: string | null;
    overlay_x: number | null;
    overlay_y: number | null;
    overlay_width: number | null;
    overlay_height: number | null;
    background_image: string | null;
    background_x: number | null;
    background_y: number | null;
    background_width: number | null;
    background_height: number | null;
    inset_width: number | null;
    inset_height: number | null;
    inset_x: number | null;
    inset_y: number | null;
    design?: Design | null;
  };
}

/**
 * Product with brand information for display purposes
 * Combines elements of DisplayProduct with additional brand data
 */
export interface ProductWithBrand {
  id: string;
  title: string;
  part_number: string;
  moq: number;
  description: string;
  features: string[];
  images: string[];
  hover_image: string | null;
  pdf_attachments: PdfAttachment[] | null;
  tax_rate: number;
  vehicle_types: {
    value: string;
    label: string;
    avatar: string | null;
  }[];
  price: number;
  compare_price: number;
  attachments: {
    id: string;
    name: string;
    quantity: number;
  }[];
  attribute_values: {
    id: string;
    value: string;
    label: string;
  }[];
  tags: string[];
  brand: string;
  weight: number;
  width: number | null;
  height: number | null;
  length: number | null;
  shipping_width: number | null;
  shipping_height: number | null;
  shipping_length: number | null;
  shipping_weight: number;
  meta_title: string | null;
  meta_description: string | null;
  editor: EditorType | null;
  product_brand?: ProductBrand;
}

/**
 * Adapter function to convert Product to ProductWithBrand
 * This is used to fix type compatibility issues between different product representations
 */
export function adaptProductToProductWithBrand(
  product: Product
): ProductWithBrand {
  return {
    id: product.id,
    title: product.title,
    part_number: product.part_number,
    moq: product.moq,
    description: product.description,
    features: product.features || [],
    images: product.images || [],
    hover_image: product.hover_image || null,
    pdf_attachments: product.pdf_attachments || null,
    tax_rate: product.tax_rate,
    vehicle_types:
      product.vehicle_types?.map((vt) => ({
        value: vt.vehicle_type_id,
        label: "", // This will be filled from the product_brand if available
        avatar: null,
      })) || [],
    price: product.product_pricing_tiers?.[0]?.base_price || 0,
    compare_price: product.product_pricing_tiers?.[0]?.compare_price || 0,
    attachments:
      product.attachments?.map((a) => ({
        id: a.id ?? "",
        name: "", // This would need to be filled from the attached product data
        quantity: a.quantity,
      })) || [],
    attribute_values:
      product.attribute_values?.map((av) => ({
        id: av.id,
        value: av.value,
        label: "", // This would need to be filled from the attribute data
      })) || [],
    tags: product.tags?.map((t) => t.name ?? "").filter(Boolean) || [],
    brand: product.product_brand?.name || "",
    weight: product.weight,
    width: product.width || null,
    height: product.height || null,
    length: product.length || null,
    shipping_width: product.shipping_width || null,
    shipping_height: product.shipping_height || null,
    shipping_length: product.shipping_length || null,
    shipping_weight: product.shipping_weight,
    meta_title: product.meta_title || null,
    meta_description: product.meta_description || null,
    editor: product.editor || null,
    product_brand: product.product_brand,
  };
}
